import * as React from 'react'
import * as Redux from 'react-redux'
import styled from 'styled-components'

import * as api from '@owl-nest/api-client/latest'
import * as models from '@owl-nest/models'
import * as config from '@owl-nest/config'
import { t } from '@owl-nest/localize'
import * as plume from '@ulule/owl-kit-components/next'

import { AddNews } from './AddNews'
import { SidebarNewsCTA, SidebarRewardsCTA, SidebarDonationCTA } from '../../components/Sidebar'
import { Event } from './Event'
import * as Styles from '../../style'
import { RootState } from '../../../../../reducers'
import { State as EventState } from '../../../../../reducers/events'
import * as eventAction from '../../../../../actions/events'
import { useListLoadingIndicator } from '../../hooks/useListLoadingIndicator'
import { TRACKING_SOURCE } from '../../utils/trackerEnums'
import { DisplayMode } from '../../../../context'

import type { Project } from '../../../../models/project'
import { NewsCard } from './NewsCard'

export function NewsTab(): React.ReactElement<unknown> {
  const project = Redux.useSelector<RootState, Project>((state) => state.application.project.data)
  const events = Redux.useSelector<RootState, EventState>((state) => state.application.events)
  const dispatch = Redux.useDispatch()
  const isEditMode = React.useContext(DisplayMode) === 'edit'
  const userConfig = config.useUserEnv()

  React.useEffect(() => {
    dispatch(
      eventAction.load(project, {
        pagination: `?limit=${5}&since=${Math.floor(Date.now() / 1000)}`,
        forceClean: true,
        loadDrafts: true,
      }),
    )
  }, [])

  const list = events.list
  const hasMore = list && list.next !== null
  const hasNews = list && list.events.some((event) => event.type === api.EventType.NEWS)
  const [isLoading, markLoadingStart] = useListLoadingIndicator(list?.events.length)

  const [activeVideo, setActiveVideo] = React.useState<number>()

  return (
    <Styles.Main isEditMode={isEditMode}>
      <Styles.Wrapper isEditMode={isEditMode}>
        <Styles.Content isEditMode={isEditMode}>
          <Feed>
            {isEditMode ? <AddNews /> : !hasNews && <FullNewsCTA source={TRACKING_SOURCE.NEWS} />}

            {events.draftNews.map((news) => {
              return (
                <NewsCard
                  key={news.id}
                  isEditMode={isEditMode}
                  isVideoActive={activeVideo === news.id}
                  onVideoMount={() => setActiveVideo(news.id)}
                  locale={userConfig.lang}
                  news={news}
                  project={project}
                />
              )
            })}
            {list?.events.map((event) => {
              if (event.type === api.EventType.NEWS) {
                const news = event.data

                return (
                  <NewsCard
                    key={news.id}
                    isEditMode={isEditMode}
                    isVideoActive={activeVideo === news.id}
                    onVideoMount={() => setActiveVideo(news.id)}
                    locale={userConfig.lang}
                    news={news}
                    project={project}
                  />
                )
              } else {
                return <Event key={Math.random()} event={event} />
              }
            })}
          </Feed>
          {hasMore && list && (
            <SeeMoreWrapper>
              <plume.Button
                kind="secondary"
                loading={isLoading}
                onClick={() => {
                  if (list.next) {
                    dispatch(eventAction.load(project, { pagination: list.next }))
                    markLoadingStart()
                  }
                }}
              >
                {t('See more')}
              </plume.Button>
            </SeeMoreWrapper>
          )}
        </Styles.Content>
        <Styles.Sidebar isEditMode={isEditMode}>
          {models.project.isDonationBased(project) ? <SidebarDonationCTA /> : <SidebarRewardsCTA />}
          {(isEditMode || hasNews) && <SidebarNewsCTA source={TRACKING_SOURCE.NEWS} />}
        </Styles.Sidebar>
      </Styles.Wrapper>
    </Styles.Main>
  )
}

const FullNewsCTA = styled(SidebarNewsCTA)`
  max-width: 620px;
  margin-left: auto;
  margin-right: auto;
`

const Feed = styled.div`
  & > *:first-child {
    margin-top: 0;
    &::before {
      top: 0;
    }
  }

  & > *:last-child {
    margin-bottom: 0;
    &::before {
      bottom: 0;
    }
  }
`

const SeeMoreWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
`
